import {
  Component,
  inject,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  QueryList,
  ViewChildren,
  PLATFORM_ID,
  HostListener,
} from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { GloButtonComponent } from "../../../../shared/components/glo-button/glo-button.component";
import { GloControlComponent } from "../../../../shared/components/glo-control/glo-control.component";
import { LandingOurBlogComponent } from "../../shared/our-blog/our-blog.component";
import { GloCarouselComponent } from "../../../../shared/carousel/carousel.component";
import { GloDateControl } from "../../../../shared/components/glo-date-control/glo-date-control.component";
import { GloTimepickerComponent } from "../../../../shared/glo-timepicker/glo-timepicker.component";
import { GloCarouselArrowComponent } from "../../../../shared/carousel-arrow/carousel-arrow.component";
import { CardLandingComponent } from "../../../../shared/components/card-landing/card-landing.component";
import { Salon } from "../../../../shared/models/salon.model";
import { CardSkeletonComponent } from "../../shared/card-skeleton/card-skeleton.component";
import { EmptyComponent } from "../../../../shared/components/empty/empty.component";
import { AuthService } from "../../../../shared/services/auth.service";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { environment } from "../../../../../environments/environment";
import { AiBookingCardComponent } from "../../../../shared/components/ai-booking-card/ai-booking-card/ai-booking-card.component";
import { GoogleAddressComponent } from "../../../../shared/google-address/google-address.component";
import { FormsModule } from "@angular/forms";

type SearchSuggestionKind = "salon" | "service" | "type" | "tag";

interface SearchSuggestion {
  label: string;
  value: string;
  kind: SearchSuggestionKind;
}

@Component({
  selector: "landing-customer-main",
  standalone: true,
  imports: [
    LoaderComponent,
    CommonModule,
    FormsModule,
    GloControlComponent,
    GloButtonComponent,
    LandingOurBlogComponent,
    GloCarouselComponent,
    GloCarouselArrowComponent,
    GloDateControl,
    GloTimepickerComponent,
    CardLandingComponent,
    CardSkeletonComponent,
    EmptyComponent,
    AiBookingCardComponent,
    GoogleAddressComponent,
  ],
  templateUrl: "./main.component.html",
  styleUrl: "./main.component.scss",
})
export class LandingCustomerMainComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChildren("revealSection")
  revealSections!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren("revealItem")
  revealItems!: QueryList<ElementRef<HTMLElement>>;

  private revealObserver?: IntersectionObserver;
  private itemObserver?: IntersectionObserver;

  private http = inject(HttpClient);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  apiUrl = environment.apiUrl;
  assetUrl = environment.assetBaseUrl;

  demoRequest = {
    name: "",
    email: "",
  };

  demoSubmitting = false;
  demoSuccess = false;
  demoError = "";

  submitDemoRequest(): void {
    if (this.demoSubmitting) {
      return;
    }

    const name = this.demoRequest.name.trim();
    const email = this.demoRequest.email.trim();

    if (!name || name.length < 2 || !email) {
      this.demoError = "Please enter your name and email address.";
      return;
    }

    this.demoSubmitting = true;
    this.demoSuccess = false;
    this.demoError = "";

    this.http
      .post(`${this.apiUrl}/public/demo-request/`, {
        name,
        email,
      })
      .subscribe({
        next: () => {
          this.demoSubmitting = false;
          this.demoSuccess = true;

          this.demoRequest = {
            name: "",
            email: "",
          };
        },
        error: (error) => {
          console.error("Demo request failed:", error);

          this.demoSubmitting = false;
          this.demoError = "We couldn't submit your request. Please try again.";
        },
      });
  }

  /**
   * Decorative hero photo-reel imagery.
   * Each vertical column renders this ordered set TWICE so the CSS
   * translate(50%) animation loops seamlessly. The two columns use different
   * orderings so they don't look identical.
   */
  readonly heroReelImages: string[] = [
    "img/salon-details1.png",
    "img/customer-photo.png",
    "img/whyus2.png",
    "img/salon.png",
    "img/find-img1.png",
    "img/salon-details2.png",
    "img/mock-salon1.png",
    "img/whyus3.png",
  ];

  /** Left (down-scrolling) column — set duplicated once for a seamless loop. */
  get heroReelLeftSet(): string[] {
    return [...this.heroReelImages, ...this.heroReelImages];
  }

  /** Right (up-scrolling) column — order offset + duplicated for looping. */
  get heroReelRightSet(): string[] {
    const base = this.heroReelImages;
    const offset = 3;
    const shifted = [...base.slice(offset), ...base.slice(0, offset)];
    return [...shifted, ...shifted];
  }

  salons: Salon[] = [];
  loading = true;
  error: string | null = null;

  serviceQuery = "";
  locationQuery = "";

  // Cost filter options (1 = $ … 4 = $$$$). "" = Any price (no cost filter).
  costOptions = [
    { id: "", name: "Any price" },
    { id: "1", name: "$" },
    { id: "2", name: "$$" },
    { id: "3", name: "$$$" },
    { id: "4", name: "$$$$" },
  ];
  selectedCost = "";

  selectedSearchDate: string | null = null;
  selectedSearchStartTime: string | null = null;
  selectedSearchEndTime: string | null = null;

  serviceSuggestions: SearchSuggestion[] = [];
  showServiceSuggestions = false;

  constructor(private AuthService: AuthService) {}

  ngOnInit(): void {
    if (this.isBrowser()) {
      const storedCity = localStorage.getItem("gloget_city")?.trim();

      if (storedCity && !this.locationQuery.trim()) {
        this.locationQuery = storedCity;
      }
    }

    this.loadSalons();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser()) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.initRevealAnimations();
    });

    this.revealSections.changes.subscribe(() => {
      window.requestAnimationFrame(() => {
        this.initRevealAnimations();
      });
    });

    this.revealItems.changes.subscribe(() => {
      window.requestAnimationFrame(() => {
        this.initRevealAnimations();
      });
    });
  }

  ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.itemObserver?.disconnect();
  }

  onLocationPlaceSelected(place: google.maps.places.PlaceResult): void {
    const components = place.address_components ?? [];

    const city =
      components.find((component) => component.types.includes("locality"))
        ?.long_name ||
      components.find((component) => component.types.includes("postal_town"))
        ?.long_name ||
      components.find((component) =>
        component.types.includes("administrative_area_level_3"),
      )?.long_name ||
      place.name ||
      "";

    this.locationQuery = city.trim();

    if (this.locationQuery && this.isBrowser()) {
      localStorage.setItem("gloget_city", this.locationQuery);
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private initRevealAnimations(): void {
    if (!this.isBrowser()) {
      return;
    }

    this.initSectionRevealAnimations();
    this.initItemPopAnimations();
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.showServiceSuggestions = false;
  }

  onServiceQueryInput(value: string): void {
    this.serviceQuery = value || "";

    this.serviceSuggestions = this.buildServiceSuggestions(this.serviceQuery);

    this.showServiceSuggestions =
      !!this.serviceQuery.trim() && this.serviceSuggestions.length > 0;
  }

  selectServiceSuggestion(suggestion: SearchSuggestion): void {
    this.serviceQuery = suggestion.value;

    this.showServiceSuggestions = false;
  }

  private buildServiceSuggestions(query: string): SearchSuggestion[] {
    const q = this.normalizeSearchText(query);

    if (!q) {
      return [];
    }

    const seen = new Set<string>();

    const suggestions: Array<
      SearchSuggestion & {
        score: number;
      }
    > = [];

    for (const salon of this.salons) {
      const salonName = salon?.name ?? "";

      const businessType =
        (salon as any)?.business_type ??
        (salon as any)?.businessType ??
        (salon as any)?.business?.business_type ??
        "";

      const services = this.getServiceNames(salon);

      const tags = this.tagsAsStrings((salon as any)?.tags);

      const candidates: Array<{
        text: string;
        kind: SearchSuggestionKind;
        baseScore: number;
      }> = [
        {
          text: salonName,
          kind: "salon",
          baseScore: 10,
        },
        {
          text: businessType,
          kind: "type",
          baseScore: 7,
        },

        ...services.map((service) => ({
          text: service,
          kind: "service" as const,
          baseScore: 8,
        })),

        ...tags.map((tag) => ({
          text: tag,
          kind: "tag" as const,
          baseScore: 6,
        })),
      ];

      for (const candidate of candidates) {
        const raw = candidate.text?.trim();

        if (!raw) {
          continue;
        }

        const normalized = this.normalizeSearchText(raw);

        if (!normalized) {
          continue;
        }

        let score = 0;

        if (normalized === q) {
          score = candidate.baseScore + 20;
        } else if (normalized.startsWith(q)) {
          score = candidate.baseScore + 12;
        } else if (normalized.includes(q)) {
          score = candidate.baseScore + 7;
        } else {
          const queryTokens = this.tokenizeSearch(q);

          const candidateTokens = this.tokenizeSearch(normalized);

          const tokenHit = queryTokens.some((queryToken) =>
            candidateTokens.some((candidateToken) =>
              this.searchTokenMatches(queryToken, candidateToken),
            ),
          );

          if (tokenHit) {
            score = candidate.baseScore + 4;
          }
        }

        if (score <= 0) {
          continue;
        }

        const key = `${candidate.kind}::${normalized}`;

        if (seen.has(key)) {
          continue;
        }

        seen.add(key);

        suggestions.push({
          label: raw,
          value: raw,
          kind: candidate.kind,
          score,
        });
      }
    }

    return suggestions
      .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
      .slice(0, 8)
      .map(({ label, value, kind }) => ({
        label,
        value,
        kind,
      }));
  }

  private normalizeSearchText(value: any): string {
    return (value ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/&/g, " and ")
      .replace(/[^\w\s-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private tokenizeSearch(value: any): string[] {
    return this.normalizeSearchText(value)
      .split(" ")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private searchTokenMatches(token: string, candidate: string): boolean {
    if (!token || !candidate) {
      return false;
    }

    if (candidate === token) {
      return true;
    }

    if (candidate.includes(token)) {
      return true;
    }

    if (token.includes(candidate) && candidate.length >= 4) {
      return true;
    }

    const tokenStem = token.length >= 4 ? token.slice(0, 4) : token;

    const candidateStem =
      candidate.length >= 4 ? candidate.slice(0, 4) : candidate;

    return !!tokenStem && tokenStem === candidateStem;
  }

  private getServiceNames(salon: any): string[] {
    if (!Array.isArray(salon?.services)) {
      return [];
    }

    return salon.services
      .map(
        (service: any) =>
          service?.name ?? service?.service_name ?? service?.title ?? service,
      )
      .filter(Boolean);
  }

  private tagText(tag: any): string {
    if (tag == null) {
      return "";
    }

    if (typeof tag === "string" || typeof tag === "number") {
      return String(tag);
    }

    if (typeof tag === "object") {
      return (
        tag.name ??
        tag.title ??
        tag.label ??
        tag.value ??
        tag.tag ??
        tag.text ??
        ""
      ).toString();
    }

    return "";
  }

  private tagsAsStrings(tags: any): string[] {
    if (!Array.isArray(tags)) {
      return [];
    }

    return tags.map((tag) => this.tagText(tag)).filter(Boolean);
  }

  private initSectionRevealAnimations(): void {
    this.revealObserver?.disconnect();

    const sections = this.revealSections?.toArray() ?? [];

    if (!sections.length) return;

    if (typeof IntersectionObserver === "undefined") {
      sections.forEach((section) => {
        section.nativeElement.classList.add("is-visible");
        section.nativeElement.classList.remove("reveal-pending");
      });
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;

          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            el.classList.remove("reveal-pending");
            this.revealObserver?.unobserve(el);
          }
        });
      },
      {
        threshold: 0.08,
        rootMargin: "0px 0px -12% 0px",
      },
    );

    sections.forEach((section, index) => {
      const el = section.nativeElement;

      el.style.setProperty("--reveal-delay", `${index * 60}ms`);
      el.classList.add("reveal");

      const rect = el.getBoundingClientRect();
      const isAlreadyInView =
        rect.top < window.innerHeight * 0.92 && rect.bottom > 0;

      if (isAlreadyInView) {
        el.classList.add("is-visible");
        el.classList.remove("reveal-pending");
      } else {
        el.classList.add("reveal-pending");
        el.classList.remove("is-visible");
        this.revealObserver?.observe(el);
      }
    });
  }

  private initItemPopAnimations(): void {
    this.itemObserver?.disconnect();

    const items = this.revealItems?.toArray() ?? [];

    if (!items.length) return;

    if (typeof IntersectionObserver === "undefined") {
      items.forEach((item) => {
        item.nativeElement.classList.add("pop-visible");
      });
      return;
    }

    this.itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;

          if (entry.isIntersecting) {
            el.classList.add("pop-visible");
            this.itemObserver?.unobserve(el);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -8% 0px",
      },
    );

    items.forEach((item, index) => {
      const el = item.nativeElement;

      el.style.setProperty("--pop-delay", `${Math.min(index % 8, 7) * 80}ms`);
      el.classList.add("pop-row");

      const rect = el.getBoundingClientRect();
      const isAlreadyInView =
        rect.top < window.innerHeight * 0.94 && rect.bottom > 0;

      if (isAlreadyInView) {
        el.classList.add("pop-visible");
      } else {
        el.classList.remove("pop-visible");
        this.itemObserver?.observe(el);
      }
    });
  }

  onSearchDateChange(date: string): void {
    this.selectedSearchDate = date;
  }

  onSearchTimeChange(value: { startTime: string; endTime: string }): void {
    this.selectedSearchStartTime = value?.startTime || null;

    this.selectedSearchEndTime = value?.endTime || null;
  }

  loadSalons(): void {
    this.loading = true;
    this.error = null;

    const allSalons: Salon[] = [];

    const fetchPage = (url: string) => {
      this.http.get<any>(url).subscribe({
        next: (response) => {
          const results = response?.results ?? [];
          const nextUrl = response?.next ?? null;

          allSalons.push(...results);

          if (nextUrl) {
            fetchPage(nextUrl);
          } else {
            this.salons = allSalons;
            this.loading = false;

            if (this.isBrowser()) {
              window.requestAnimationFrame(() => {
                this.initRevealAnimations();
              });
            }
          }
        },
        error: (error) => {
          console.error("Error loading salons:", error);
          this.error = "Failed to load salons";
          this.loading = false;
        },
      });
    };

    fetchPage(`${this.apiUrl}/public/salons/`);
  }

  get isAuthenticated(): boolean {
    if (!this.isBrowser()) {
      return !!this.AuthService.user;
    }

    return !!this.AuthService.user || !!localStorage.getItem("user");
  }

  onAiSearchRequested(payload: {
    category?: string | null;
    location?: string | null;
  }): void {
    const queryParams: any = {};

    if (payload.category) {
      queryParams.category = payload.category;
      this.serviceQuery = payload.category;
    }

    if (payload.location) {
      queryParams.location = payload.location;
      this.locationQuery = payload.location;
    }

    this.router.navigate(["/customer/search"], { queryParams });
  }

  quickSearch(category: string, location: string): void {
    this.serviceQuery = category;
    this.locationQuery = location;
    this.searchSalons();
  }

  searchNearMe(): void {
    const now = new Date();

    const pad = (value: number): string => String(value).padStart(2, "0");

    // Today's date in YYYY-MM-DD (the format the search page expects).
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate(),
    )}`;

    // Start at the current hour and run through the end of the day (HH:MM, 24h).
    const startTime = `${pad(now.getHours())}:00`;
    const endTime = "23:00";

    // Use the same area shown in the header (the user's detected city).
    const storedCity = this.isBrowser()
      ? localStorage.getItem("gloget_city")?.trim()
      : "";

    const queryParams: Record<string, string> = {
      date,
      start_time: startTime,
      end_time: endTime,
    };

    if (storedCity) {
      queryParams["location"] = storedCity;
    }

    if (this.selectedCost) {
      queryParams["cost"] = this.selectedCost;
    }

    this.router.navigate(["/customer/search"], { queryParams });
  }

  private normalize(value: any): string {
    return (value ?? "").toString().trim();
  }

  searchSalons(): void {
    const category = this.normalize(this.serviceQuery);

    const location = this.normalize(this.locationQuery);

    const queryParams: any = {};

    if (category) {
      queryParams.category = category;
    }

    if (location) {
      queryParams.location = location;
    }

    if (this.selectedCost) {
      queryParams.cost = this.selectedCost;
    }

    if (this.selectedSearchDate) {
      queryParams.date = this.selectedSearchDate;
    }

    if (this.selectedSearchStartTime) {
      queryParams.start_time = this.selectedSearchStartTime;
    }

    if (this.selectedSearchEndTime) {
      queryParams.end_time = this.selectedSearchEndTime;
    }

    this.router.navigate(["/customer/search"], { queryParams });
  }

  handleToggle(salonObject: { salonId: string; isFavorite: boolean }): void {
    this.salons = this.salons.map((salon) => {
      if (salon.salon_id === salonObject.salonId) {
        return {
          ...salon,
          is_favorite: salonObject.isFavorite,
        };
      }

      return salon;
    });
  }

  onLoad(state: boolean): void {
    this.loading = state;
  }

  trackBySalonId(index: number, salon: Salon): string {
    return salon.salon_id;
  }

  trackByHeroReelPhoto(index: number): number {
    return index;
  }

  retryLoadSalons(): void {
    this.loadSalons();
  }
}
